import { FunnelStageEnum } from "@sidegigllc/api/client";
import { createRoute, lazyRouteComponent, redirect } from "@tanstack/react-router";
import { applicantFilterQueryParamsSchema, applicantOrderingSchema, jobQueryParamsSchema } from "api/resources/hire";
import { requireLocationManager } from "domains/auth/access/locationManager";
import { hasJobsAccess, isJobsRolledOut } from "domains/hire/access/jobsAccess";
import { statusIds, typeIds } from "domains/hire/components/HiringScheduleFilters/constants";
import { candidatesSearchSchema } from "domains/hire/containers/Candidates/routing";
import { isScheduleInterviewsEnabled } from "domains/hire/hooks/useScheduleInterviewsEnabled";
import { isJobsLocked } from "domains/hire/jobsPlan";
import { queries as candidatesQueries } from "domains/hire/views/Candidates/queries";
import { queries as hiringScheduleQueries } from "domains/hire/views/HiringSchedule/queries";
import { queries as jobDetailsQueries } from "domains/hire/views/JobDetails/queries";
import { queries as jobListQueries } from "domains/hire/views/JobList/queries";
import { queries as scheduleInterviewsQueries } from "domains/hire/views/ScheduleInterviews/queries";
import { offerTypeSchema } from "domains/pros/containers/SendDirectOfferDialog/routing";
import { getFeatureFlag } from "providers/LaunchDarkly/client";
import { warmQueries } from "routing/warmQueries";
import * as z from "zod/mini";
import { locationLayoutRoute } from "./locations";
import { makeMessagingRoutes, messagingInboxRouteOptions } from "./messaging";

const jobsSearchSchema = z.extend(jobQueryParamsSchema, { new: z.optional(z.boolean()) });

const applicantSearchSchema = z.extend(applicantFilterQueryParamsSchema, {
  ordering: z.optional(applicantOrderingSchema),
  funnel_stage: z.optional(z.enum(FunnelStageEnum)),
  applicant_id: z.optional(z.string()),
  offerType: offerTypeSchema,
});
export type ApplicantSearch = z.infer<typeof applicantSearchSchema>;

// type/status/availability are cleared to undefined by the filters toolbar, so they stay optional
// (a .default() would make them required in the output type and break the clearing setter); their
// display defaults live at the read sites.
const hiringScheduleSearchSchema = z.object({
  calendar_date: z._default(z.coerce.date(), () => new Date()),
  interview_id: z.optional(z.string()),
  type: z.optional(z.array(z.enum([typeIds[0], ...typeIds]))),
  status: z.optional(z.array(z.enum([statusIds[0], ...statusIds]))),
  availability: z.optional(z.string()),
});

const scheduleInterviewsSearchSchema = z.object({ applicants: z.optional(z.string()) });

const redirectToJobs = (locationId: string, searchStr: string): never => {
  throw redirect({ href: `/locations/${locationId}/hire/jobs${searchStr}` });
};

const hireRoot = createRoute({
  getParentRoute: () => locationLayoutRoute,
  path: "hire",
  beforeLoad: async ({ context: { auth, queryClient, location }, params }) => {
    if (!location) throw redirect({ href: "/locations" });
    const jobsPhased = await getFeatureFlag("jobs-phased", { queryClient });
    if (!isJobsRolledOut({ jobsPhased, jobsEnabled: location.jobs_enabled })) throw redirect({ href: "/" });
    requireLocationManager(auth.user, Number(params.locationId));
    return { location };
  },
});

const hireIndexRoute = createRoute({
  getParentRoute: () => hireRoot,
  path: "/",
  beforeLoad: ({ params, location }) => redirectToJobs(params.locationId, location.searchStr),
});

// jobs / jobs/$jobId / jobs/$jobId/schedule are flat siblings, not nested: none renders an
// Outlet, and nesting would merge their search so the list and detail `ordering` params would collide.
export const jobsRoute = createRoute({
  getParentRoute: () => hireRoot,
  path: "jobs",
  validateSearch: jobsSearchSchema,
  loaderDeps: ({ search: { new: _new, ...search } }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, jobListQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/hire/views/JobList"), "JobListView"),
});

export const jobDetailsRoute = createRoute({
  getParentRoute: () => hireRoot,
  path: "jobs/$jobId",
  validateSearch: applicantSearchSchema,
  // offerType drives the direct-offer dialog only; excluding it keeps the loader from re-running when
  // the sheet toggles, and keeps it out of the applicants-list query params.
  loaderDeps: ({ search: { offerType: _offerType, ...applicant } }) => applicant,
  loader: ({ params, deps, context }) =>
    warmQueries(
      context.queryClient,
      jobDetailsQueries({
        jobId: params.jobId,
        locationId: Number(params.locationId),
        search: deps,
        locked: isJobsLocked(context.location.jobs_subscription, !!context.auth.user.is_gigpro_admin),
      }),
    ),
  component: lazyRouteComponent(() => import("domains/hire/views/JobDetails"), "JobDetailsView"),
});

export const scheduleInterviewsRoute = createRoute({
  getParentRoute: () => hireRoot,
  path: "jobs/$jobId/schedule",
  validateSearch: scheduleInterviewsSearchSchema,
  loaderDeps: ({ search }) => ({ applicants: search.applicants }),
  loader: ({ params, deps, context }) => {
    const applicantIds = deps.applicants?.split(",").filter(Boolean) ?? [];
    // No selection carried in the URL means the page was reached without picking applicants — bounce back.
    if (applicantIds.length === 0) {
      throw redirect({
        to: "/locations/$locationId/hire/jobs/$jobId",
        params: { locationId: params.locationId, jobId: params.jobId },
      });
    }
    warmQueries(
      context.queryClient,
      scheduleInterviewsQueries({ jobId: params.jobId, locationId: Number(params.locationId), applicantIds }),
    );
  },
  component: lazyRouteComponent(() => import("domains/hire/views/ScheduleInterviews"), "ScheduleInterviewsView"),
});

export const candidatesRoute = createRoute({
  getParentRoute: () => hireRoot,
  path: "candidates",
  validateSearch: candidatesSearchSchema,
  // With Jobs in the main menu the list lives on Connect's Applicants tab; its group and favorites views go away
  beforeLoad: async ({ params, search, context }) => {
    const [jobsInMainMenu, jobsPhased] = await Promise.all([
      getFeatureFlag("jobs-in-main-menu", { queryClient: context.queryClient }),
      getFeatureFlag("jobs-phased", { queryClient: context.queryClient }),
    ]);
    const hasAccess = hasJobsAccess({
      jobsPhased,
      jobsEnabled: context.location.jobs_enabled,
      user: context.auth.user,
      locationId: Number(params.locationId),
    });
    if (!jobsInMainMenu || !hasAccess) return;
    const { tab: _tab, group_id: _group_id, ...rest } = search;
    throw redirect({
      to: "/locations/$locationId/connect/applicants",
      params: { locationId: params.locationId },
      search: rest,
    });
  },
  // group_id/candidate_id drive the sheet/tab UI only; buildCandidatesListParams discards them, so
  // excluding them here stops a card-select from needlessly re-running the list fetch.
  loaderDeps: ({ search: { offerType: _offerType, group_id: _group_id, candidate_id: _candidate_id, ...rest } }) =>
    rest,
  loader: ({ deps, context }) => warmQueries(context.queryClient, candidatesQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/hire/views/Candidates"), "CandidatesView"),
});

export const scheduleRoute = createRoute({
  getParentRoute: () => hireRoot,
  path: "schedule",
  validateSearch: hiringScheduleSearchSchema,
  // Interviews live on the Schedule, which replaces this page; its links keep the date and the open interview.
  beforeLoad: async ({ params, search, context }) => {
    const [jobsInMainMenu, jobsPhased] = await Promise.all([
      getFeatureFlag("jobs-in-main-menu", { queryClient: context.queryClient }),
      getFeatureFlag("jobs-phased", { queryClient: context.queryClient }),
    ]);
    const hasAccess = hasJobsAccess({
      jobsPhased,
      jobsEnabled: context.location.jobs_enabled,
      user: context.auth.user,
      locationId: Number(params.locationId),
    });
    if (!isScheduleInterviewsEnabled(jobsInMainMenu, hasAccess)) return;
    throw redirect({
      to: "/locations/$locationId/schedule",
      params: { locationId: params.locationId },
      search: { calendar_date: search.calendar_date, interview_id: search.interview_id },
    });
  },
  loaderDeps: ({ search }) => ({ calendar_date: search.calendar_date, availability: search.availability }),
  loader: ({ deps, context }) =>
    warmQueries(
      context.queryClient,
      hiringScheduleQueries({ calendarDate: deps.calendar_date, availability: deps.availability }),
    ),
  component: lazyRouteComponent(() => import("domains/hire/views/HiringSchedule"), "HiringScheduleView"),
});

const hireInboxRoute = createRoute({
  getParentRoute: () => hireRoot,
  path: "inbox",
  ...messagingInboxRouteOptions,
  component: lazyRouteComponent(() => import("domains/hire/views/Inbox"), "HireInbox"),
});

const hireCatchAllRoute = createRoute({
  getParentRoute: () => hireRoot,
  path: "$",
  beforeLoad: ({ params, location }) => redirectToJobs(params.locationId, location.searchStr),
});

export const hireRoutes = hireRoot.addChildren([
  hireIndexRoute,
  jobsRoute,
  jobDetailsRoute,
  scheduleInterviewsRoute,
  candidatesRoute,
  scheduleRoute,
  hireInboxRoute.addChildren([makeMessagingRoutes(hireInboxRoute)]),
  hireCatchAllRoute,
]);
